import {} from "_graphql/queries/__generated__/GetOfferAssociates";

import {
  ActionButton,
  Avatar,
  OfficeButton,
  Show,
  TableComponent,
} from "components";
import { Link, useMatch, useNavigate, useSearch } from "react-location";
import { classNames, useUrlState, withPermissions } from "utils";
import { useTreaty, useTreatyReinsurers } from "hooks/data/use-treaties";

import { Action } from "components/buttons/action-button";
import AddParticipantsPercentageContainer from "./add-reinsurer-percentage";
import { LocationGenerics } from "router/location";
import RemoveReinsurerContainer from "./remove-reinsurer";
import SendDocumentsContainer from "./send-documents";
import SendDocumentsNonProportionalContainer from "./send-documents-non-proportional";
import ViewNonProportionalTreatyClosingContainer from "./view-reinsurer-non-proportional-closing";
import ViewTreatyClosingContainer from "./view-reinsurer-closings";
import _ from "lodash";
import RepresentationOfShares from "./representation-of-shares";
import { Colors } from "_graphql/data";

type TreatyReinsurersProps = {
  hideDeductionButton?: boolean;
};

export default function TreatyReinsurers({
  hideDeductionButton = false,
}: TreatyReinsurersProps) {
  const [modal, setModal] = useUrlState("modal");
  const [activeTab] = useUrlState("layer");
  const { params } = useMatch<LocationGenerics>();
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { loading, reinsurers, refetch } = useTreatyReinsurers({
    treatyId: params.treaty || "",
  });

  const { treaty, loading: featchingTreaty } = useTreaty({
    treatyId: params.treaty || "",
  });

  const allReinsurersHavePercentage =
    reinsurers.length > 0 &&
    _.every(reinsurers, (reinsurer) => {
      return (
        reinsurer?.treaty_participation_percentage &&
        reinsurer.treaty_participation_percentage > 0
      );
    });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto">) => () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const resinsurers =
    searchParams.treaty_type === "PROPORTIONAL"
      ? reinsurers
      : _.get(
          _.groupBy(reinsurers, "layer_number"),
          parseInt(searchParams.layer || "1"),
          []
        );

  return (
    <div className=" flex flex-col flex-1">
      <Show if={searchParams?.treaty_type === "NONPROPORTIONAL"}>
        <div className="block">
          <div className="border-b border-background-500 bg-card-500 px-4 sm:px-6">
            <nav
              className="-mb-px flex items-center space-x-4 sm:space-x-8 overflow-x-auto no-scrollbar whitespace-nowrap"
              aria-label="Tabs"
            >
              {JSON.parse(treaty?.layer_limit || "[]").map(
                (_activeTab: any, index: number) => (
                  <Link
                    key={_activeTab.name}
                    search={(old) => ({
                      ...old,
                      layer: (index + 1).toString(),
                    })}
                    className={classNames(
                      activeTab == index + 1
                        ? "border-gray-200 text-gray-300"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                      "shrink-0 flex py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm"
                    )}
                    aria-current={activeTab === index + 1 ? "page" : undefined}
                  >
                    Layer {index + 1}
                  </Link>
                )
              )}
              {/* <div className="h-6 w-px bg-gray-300" /> */}
            </nav>
          </div>
        </div>
      </Show>
      <div className="grid grid-cols-3 sm:grid-cols-3 sm:py-6 items-start px-3 flex-1">
        <div className="col-span-1 sm:col-span-2">
          <TableComponent
            title={"reinsurers"}
            hasSearch
            loading={loading || featchingTreaty}
            columns={[
              {
                accessor: "reinsurer_representative.reinsurer.re_company_name",
                name: "Company Name",
                render: (row, index) => (
                  <div className="flex space-x-2 items-start">
                    <Avatar
                      color={Colors.at(index!)!}
                      alt={row?.reinsurer?.re_company_name || ""}
                    />
                    <div className="flex flex-col">
                      <span className="font-bold text-xs">
                        {row?.reinsurer?.re_company_name}
                      </span>
                      <span className="font-light text-xs">
                        {row?.reinsurer?.re_company_website}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                accessor: "reinsurer_representative.rep_first_name",
                name: "Company Email",
                render: (row) => (
                  <div className="flex flex-col">
                    <span className="font-bold text-xs">
                      {row?.reinsurer?.re_company_email}
                    </span>
                  </div>
                ),
              },
              {
                accessor: "treaty_participation_percentage",
                name: "Participation (%)",
                render: (row) => (
                  <div className="flex flex-col">
                    <span className="font-bold text-xs">
                      {row?.treaty_participation_percentage ?? "0"}
                    </span>
                  </div>
                ),
              },
              {
                accessor: "",
                name: "Action",
                render: (row) =>
                  hideDeductionButton ? (
                    <ActionButton
                      action={"view"}
                      tooltip={"View Treaty Closings"}
                      onClick={dispatchAction(
                        row?.treaty_participation_id || "",
                        "clone"
                      )}
                    />
                  ) : (
                    <>
                      {withPermissions([
                        "*:*",
                        "treaties:*",
                        "treaties:manage",
                        "treaties:update",
                      ])(
                        <ActionButton
                          action={"assignPercentage"}
                          tooltip={"Assign Percentage"}
                          onClick={dispatchAction(
                            row?.treaty_participation_id || "",
                            "assignPercentage"
                          )}
                        />
                      )}
                      <Show if={!_.isEmpty(row?.treaty_participant_deductions)}>
                        <>
                          <ActionButton
                            action={"clone"}
                            tooltip={"View Treaty Documents"}
                            onClick={dispatchAction(
                              row?.treaty_participation_id || "",
                              "clone"
                            )}
                          />
                          {withPermissions([
                            "*:*",
                            "treaties:*",
                            "treaties:manage",
                            "treaties:send",
                          ])(
                            <ActionButton
                              action={"send"}
                              tooltip={"Send Credit Note"}
                              onClick={dispatchAction(
                                row?.treaty_participation_id || "",
                                "send"
                              )}
                            />
                          )}
                        </>
                      </Show>
                      {withPermissions([
                        "*:*",
                        "treaties:*",
                        "treaties:manage",
                        "treaties:remove_reinsurers",
                      ])(
                        <ActionButton
                          action={"remove"}
                          tooltip={"Remove associate"}
                          onClick={dispatchAction(
                            row?.treaty_participation_id || "",
                            "remove"
                          )}
                        />
                      )}
                    </>
                  ),
              },
            ]}
            data={resinsurers}
            renderActions={
              hideDeductionButton
                ? () => null
                : () => (
                    <OfficeButton
                      disabled={!allReinsurersHavePercentage}
                      // size="sm"
                      onClick={() => setModal("modify-deduction")}
                    >
                      <span className="inline text-xs sm:hidden">
                        Deduction
                      </span>
                      <span className="hidden sm:inline">Modify Deduction</span>
                    </OfficeButton>
                  )
            }
            refetch={refetch}
          />
        </div>
        <div className="flex flex-col items-center justify-between py-4">
          <RepresentationOfShares
            treaty={treaty}
            layer={activeTab}
            reinsurers={reinsurers}
          />
        </div>
        {searchParams?.id && searchParams?.modal === "assignPercentage" && (
          <AddParticipantsPercentageContainer
            open={modal === "assignPercentage"}
            setOpen={(val: boolean) => {
              setModal(val ? "assignPercentage" : undefined);
              // dispatchAction("", "assignPercentage")()
              refetch?.();
            }}
          />
        )}
        {searchParams?.id && searchParams?.modal === "clone" && (
          <ViewTreatyClosingContainer
            open={
              modal === "clone" && searchParams?.treaty_type === "PROPORTIONAL"
            }
            setOpen={(val: boolean) => {
              setModal(val ? "assignPercentage" : undefined);
              // dispatchAction("", "assignPercentage")()
              refetch?.();
            }}
          />
        )}
        {searchParams?.id &&
          searchParams?.modal === "send" &&
          searchParams?.treaty_type === "PROPORTIONAL" && (
            <SendDocumentsContainer
              open={
                modal === "send" && searchParams?.treaty_type === "PROPORTIONAL"
              }
              setOpen={(val: boolean) => {
                setModal(val ? "send" : undefined);
                // dispatchAction("", "assignPercentage")()
                refetch?.();
              }}
            />
          )}
        {searchParams?.id &&
          searchParams?.modal === "send" &&
          searchParams?.treaty_type !== "PROPORTIONAL" && (
            <SendDocumentsNonProportionalContainer
              open={
                modal === "send" && searchParams?.treaty_type !== "PROPORTIONAL"
              }
              setOpen={(val: boolean) => {
                setModal(val ? "send" : undefined);
                // dispatchAction("", "assignPercentage")()
                refetch?.();
              }}
            />
          )}
        {searchParams?.id &&
          searchParams?.modal === "clone" &&
          searchParams?.treaty_type === "NONPROPORTIONAL" && (
            <ViewNonProportionalTreatyClosingContainer
              open={
                modal === "clone" &&
                searchParams?.treaty_type === "NONPROPORTIONAL"
              }
              setOpen={(val: boolean) => {
                setModal(val ? "assignPercentage" : undefined);
                // dispatchAction("", "assignPercentage")()
                refetch?.();
              }}
            />
          )}

        <RemoveReinsurerContainer
          open={modal === "remove"}
          setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
        />
      </div>
    </div>
  );
}
